import { Box, Typography } from '@mui/material';

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';

import { infoIcon } from 'common/assets/images';
import { displayDateFormat } from 'common/components/Datepicker/Datepicker';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import InfoTooltip from 'common/components/Tooltip/Tooltip';
import { DATAINGESTIONTABS } from 'common/constants/DataIngestionConstants';
import { TOOLTIP_TEXT } from 'common/constants/Tooltip';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	formatNumber,
	getDateStringFromISOString,
} from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import styles from './DataIngestionDataTable.module.scss';

interface FilterDataProp {
	isLoading: boolean;
	id?: string;
	data?: any;
	fetchData: (data: any) => Promise<void>;
	pageCount: number;
}

const { FAILED } = DATAINGESTIONTABS;
const { DATA_INGESTION_FILE_UPLOAD_FAILED } = TOOLTIP_TEXT;

const DataIngestionDataTable = (props: FilterDataProp) => {
	const { isLoading, id, data, fetchData, pageCount } = props;
	const dispatch = useDispatch();
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const { tableQueryObj: tableProps, updateTableQuery } = useTableQuery({ id });

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			fetchData(data);
			updateTableQuery(data);
		}
	};

	const handleSortChange = (data: any) => {
		fetchData(data);
	};

	const columns: readonly Column[] = [
		{
			id: 'fileName',
			label: 'File name',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography className={styles['data-ingestion-table-fields']}>
					{params?.fileName}
				</Typography>
			),
		},
		{
			id: 'fileType',
			label: 'File type',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography className={styles['data-ingestion-table-fields']}>
					{params?.fileType}
				</Typography>
			),
		},
		{
			id: 'user',
			label: 'User',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography className={styles['data-ingestion-table-fields']}>
					{params?.user}
				</Typography>
			),
		},
		{
			id: 'records',
			label: 'Records',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography className={styles['data-ingestion-table-fields']}>
					{formatNumber(params?.records)}
				</Typography>
			),
		},
		{
			id: 'activityDate',
			label: 'Activity date',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography className={styles['data-ingestion-table-fields']}>
					{params?.activityDate
						? moment(getDateStringFromISOString(params.activityDate)).format(
								displayDateFormat,
						  )
						: ''}
				</Typography>
			),
		},
		{
			id: 'status',
			label: 'Status',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Box className={styles['data-ingestion-status-field']}>
					{params.status === FAILED ? (
						<Box className={styles['data-ingestion-status-field']}>
							<Typography
								className={styles['data-ingestion-status-field-color']}
							>
								{params.status}
							</Typography>

							<InfoTooltip
								className={styles['overview-info-tooltip']}
								title={
									params.message
										? params.message
										: DATA_INGESTION_FILE_UPLOAD_FAILED
								}
							>
								<img
									src={infoIcon}
									alt="info"
									className={styles['overview-info']}
								/>
							</InfoTooltip>
						</Box>
					) : (
						<Typography>{params.status}</Typography>
					)}
				</Box>
			),
		},
	];
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	return (
		<>
			<Box
				className={styles['data-ingestion-landing-box']}
				data-testid="dataIngestionTable"
			>
				<TableComponent
					defaultQuery={tableProps}
					rows={data}
					columns={columns}
					totalItems={pageCount}
					defaultSortKey=""
					defaultSortDirection="desc"
					onPageHandler={handlePageChange}
					isLoading={isLoading}
					tableStyles={{ minWidth: '46.875em', maxHeight: '46.875em' }}
					onSortHandler={handleSortChange}
					enableHeaderColor
					isRenderPagination
				/>
			</Box>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</>
	);
};
export default DataIngestionDataTable;
